/**
 * AddAccessoriesDialog — isolated dialog for adding base or type-specific accessories.
 *
 * All search / filter / selection state lives here, not in the parent.
 * Typing in the search box only re-renders this dialog component, not the
 * entire MaterialAccessories page.
 *
 * Performance notes
 * ─────────────────
 * • React.memo prevents the dialog from re-rendering when parent state
 *   changes while the dialog is closed.
 * • useDeferredValue(search) decouples the TextField keystroke render from
 *   the list-filter render. Input is always instant; list catches up async.
 * • List items use a native <div> + Checkbox instead of MUI ListItemButton
 *   + ListItemText. Each MUI ListItemButton is ~20 DOM nodes; a plain <div>
 *   is 1 node — the deferred list render goes from ~2 000 DOM ops to ~200.
 * • List is paginated (PAGE_SIZE items per page) with inline Prev / Next
 *   navigation so large datasets never flood the DOM.
 * • groupFilter is applied CLIENT-SIDE so the Group Code dropdown always
 *   shows the full list for the selected dept — changing group codes no
 *   longer requires resetting to "All" first.
 * • Autocomplete replaces MUI Select for Dept + Group Code filters, giving
 *   instant type-to-search without any extra state or debounce.
 */
import React, {
  useState, useEffect, useMemo, useRef, useCallback, useDeferredValue,
} from 'react';
import Dialog        from '@mui/material/Dialog';
import DialogTitle   from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Box           from '@mui/material/Box';
import Button        from '@mui/material/Button';
import TextField     from '@mui/material/TextField';
import Checkbox      from '@mui/material/Checkbox';
import Typography    from '@mui/material/Typography';
import LinearProgress from '@mui/material/LinearProgress';
import FormControl   from '@mui/material/FormControl';
import InputLabel    from '@mui/material/InputLabel';
import Select        from '@mui/material/Select';
import MenuItem      from '@mui/material/MenuItem';
import IconButton    from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon          from '@mui/icons-material/Search';
import NavigateBeforeIcon  from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon    from '@mui/icons-material/NavigateNext';
import CheckCircleIcon     from '@mui/icons-material/CheckCircle';
import RemoveCircleIcon    from '@mui/icons-material/RemoveCircle';
import { matchesSearch, sortByRelevance } from '../utils/searchFilter';
import * as accessoryApi         from '../api/accessoryApi';
import * as materialAccessoryApi from '../api/materialAccessoryApi';
import { useSnackbar } from '../context/SnackbarContext';

/* ─── CSS for list items ─────────────────────────────────────────────────────
 * One style block injected once; pure-CSS hover via :hover pseudo-class so
 * there are zero per-item JS event listeners.
 * ─────────────────────────────────────────────────────────────────────────── */
const LIST_STYLE = `
  .adlg-item {
    display: flex; align-items: center;
    padding: 2px 12px 2px 4px; cursor: pointer;
    border-bottom: 1px solid #f0f0f0; background: transparent;
    border-left: none; border-right: none; border-top: none;
    width: 100%; text-align: left; font-family: inherit;
    min-height: 36px;
  }
  .adlg-item:hover { background-color: #F5F5F5; }
  .adlg-item-checked { background-color: #E3F2FD; }
  .adlg-item-checked:hover { background-color: #BBDEFB; }
  .adlg-item-linked { background-color: #F1F8E9; cursor: pointer; }
  .adlg-item-linked:hover { background-color: #E8F5E9; }
  .adlg-item-toremove { background-color: #FFEBEE; cursor: pointer; }
  .adlg-item-toremove:hover { background-color: #FFCDD2; }
  .adlg-primary   { font-weight: 500; font-size: 0.875rem; line-height: 1.4; color: #212121; }
  .adlg-secondary { font-size: 0.75rem; color: #666; line-height: 1.4; }
`;

const PAGE_SIZE = 50;

/** Generate the array of page indices/ellipsis to render in the pagination bar. */
function getPageNumbers(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i);
  if (current < 4)         return [0, 1, 2, 3, 4, '…', total - 1];
  if (current > total - 5) return [0, '…', total - 5, total - 4, total - 3, total - 2, total - 1];
  return [0, '…', current - 1, current, current + 1, '…', total - 1];
}


const AddAccessoriesDialog = React.memo(function AddAccessoriesDialog({
  open,
  mode,             // 'base' | 'type'
  selectedMaterial,
  selectedType,
  baseAccessories,   // already-linked base accessories (for pre-filter and type seed)
  linkedAccessories, // already-linked type accessories (to exclude duplicates)
  departments,
  onClose,
  onBaseAdded,       // (updatedBaseList) => void
  onTypeAdded,       // (updatedTypeList) => void
}) {
  // ── Local state — never escapes to parent ─────────────────────────────────
  const [search,     setSearch]     = useState('');
  const [deptFilter, setDeptFilter] = useState(''); // dept id string
  const [groupFilter,setGroupFilter]= useState(''); // group code string
  const [checkedIds, setCheckedIds] = useState(new Set());
  const [removeIds,  setRemoveIds]  = useState(new Set()); // linked items marked for removal
  const [serverList, setServerList] = useState([]); // base mode: server-fetched
  const [fetching,   setFetching]   = useState(false);
  const [saving,     setSaving]     = useState(false);
  const [listPage,   setListPage]   = useState(0);
  const cancelRef = useRef(false);

  const { showSnackbar } = useSnackbar();

  // Decouple keystroke render from list-filter render.
  // The TextField updates immediately (urgent); list filtering defers 1-2 frames.
  // In base mode deferredSearch is NOT used for the API call — only `search` is.
  const deferredSearch = useDeferredValue(search);

  // ── Reset filters and selection each time the dialog opens ────────────────
  useEffect(() => {
    if (!open) return;
    setSearch('');
    setDeptFilter('');
    setGroupFilter('');
    setCheckedIds(new Set());
    setRemoveIds(new Set());
    setServerList([]);
    setListPage(0);
  }, [open]);

  // ── Base mode: server-side filtered fetch with 300 ms debounce ────────────
  // NOTE: groupFilter is intentionally NOT sent to the server and NOT in the
  // dependency array. Sending it would make the server return only accessories
  // in that group, which would cause groupCodes dropdown to show only the
  // selected group — the user would have to reset to "All" to see others.
  // Instead, the server is asked for dept + search results, and groupFilter is
  // applied client-side in displayList. This keeps the Group Code dropdown
  // always showing the full list for the selected dept.
  useEffect(() => {
    if (!open || mode !== 'base') return;
    cancelRef.current = false;

    const fetchAcc = async () => {
      setFetching(true);
      try {
        const params = {};
        if (deptFilter) params.departmentId = deptFilter;
        if (search)     params.search        = search;
        // groupFilter intentionally omitted — applied client-side below
        const res = await accessoryApi.getAll(params);
        if (!cancelRef.current) setServerList(res.data || res || []);
      } catch { /* silently ignore — parent will show errors */ }
      finally { if (!cancelRef.current) setFetching(false); }
    };

    const timer = setTimeout(fetchAcc, search ? 300 : 0);
    return () => { cancelRef.current = true; clearTimeout(timer); };
  }, [open, mode, deptFilter, search]); // ← groupFilter deliberately excluded

  // Reset list page whenever the filtered dataset changes
  useEffect(() => { setListPage(0); }, [deptFilter, groupFilter, deferredSearch, serverList]);

  // ── Derived values ─────────────────────────────────────────────────────────
  const linkedIds = useMemo(() => new Set(
    mode === 'base'
      ? baseAccessories.map((a) => a.id)
      : linkedAccessories.map((a) => a.id)
  ), [mode, baseAccessories, linkedAccessories]);

  const sourceList = mode === 'type' ? baseAccessories : serverList;

  // groupCodes is derived from the FULL sourceList (only dept-filtered, never
  // group-filtered) so the dropdown always shows every available code regardless
  // of which group is currently selected.
  const groupCodes = useMemo(() => Array.from(
    new Set(
      sourceList
        .filter((a) =>
          !deptFilter ||
          a.accessoryGroup?.departmentId     === Number(deptFilter) ||
          a.accessoryGroup?.department?.id   === Number(deptFilter)
        )
        .map((a) => a.accessoryGroup?.groupCode)
        .filter(Boolean)
    )
  ).sort(), [sourceList, deptFilter]);

  // Uses deferredSearch so the filter computation doesn't block the input keystroke.
  const displayList = useMemo(() => {
    const filtered = sourceList.filter((a) => {
      // Already-linked items are KEPT in the list — shown with a tick mark instead of removed
      if (mode === 'type') {
        if (deptFilter) {
          const id = a.accessoryGroup?.departmentId ?? a.accessoryGroup?.department?.id;
          if (id !== Number(deptFilter)) return false;
        }
        if (groupFilter  && a.accessoryGroup?.groupCode !== groupFilter) return false;
        if (!matchesSearch(a.name, deferredSearch)) return false;
      } else {
        if (groupFilter && a.accessoryGroup?.groupCode !== groupFilter) return false;
      }
      return true;
    });
    return sortByRelevance(filtered, deferredSearch);
  }, [sourceList, linkedIds, mode, deptFilter, groupFilter, deferredSearch]);

  const totalPages  = Math.max(1, Math.ceil(displayList.length / PAGE_SIZE));
  const safePage    = Math.min(listPage, totalPages - 1);

  // Only the items on the current page enter the DOM
  const visibleList = useMemo(
    () => displayList.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE),
    [displayList, safePage]
  );

  // ── Select-All for the current page ───────────────────────────────────────
  const selectableVisible = useMemo(
    () => visibleList.filter((a) => !linkedIds.has(a.id)),
    [visibleList, linkedIds]
  );
  const linkedVisible = useMemo(
    () => visibleList.filter((a) => linkedIds.has(a.id)),
    [visibleList, linkedIds]
  );

  // "All selected" = every selectable item is checked AND every linked item is marked for removal
  const allVisibleChecked = visibleList.length > 0
    && selectableVisible.every((a) => checkedIds.has(a.id))
    && linkedVisible.every((a) => removeIds.has(a.id));
  const someVisibleChecked = (!allVisibleChecked) && (
    selectableVisible.some((a) => checkedIds.has(a.id)) ||
    linkedVisible.some((a) => removeIds.has(a.id))
  );

  const toggleSelectAll = useCallback(() => {
    if (allVisibleChecked) {
      // Deselect everything on this page
      setCheckedIds((prev) => { const n = new Set(prev); selectableVisible.forEach((a) => n.delete(a.id)); return n; });
      setRemoveIds((prev)  => { const n = new Set(prev); linkedVisible.forEach((a) => n.delete(a.id));    return n; });
    } else {
      // Select everything on this page
      setCheckedIds((prev) => { const n = new Set(prev); selectableVisible.forEach((a) => n.add(a.id)); return n; });
      setRemoveIds((prev)  => { const n = new Set(prev); linkedVisible.forEach((a) => n.add(a.id));    return n; });
    }
  }, [allVisibleChecked, selectableVisible, linkedVisible]);

  // Toggle a new (non-linked) item in/out of the to-add set
  const toggle = useCallback((id) => {
    setCheckedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  // Toggle a linked item in/out of the to-remove set
  const toggleRemove = useCallback((id) => {
    setRemoveIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  // ── Filter handlers ───────────────────────────────────────────────────────
  const handleDeptChange  = useCallback((e) => {
    setDeptFilter(e.target.value);
    setGroupFilter('');
  }, []);
  const handleGroupChange = useCallback((e) => {
    setGroupFilter(e.target.value);
  }, []);

  // ── Save handler ──────────────────────────────────────────────────────────
  const handleAdd = async () => {
    if (checkedIds.size === 0 && removeIds.size === 0) return;
    setSaving(true);
    try {
      const addList    = Array.from(checkedIds);
      const removeList = Array.from(removeIds);

      if (mode === 'base') {
        if (addList.length)    await materialAccessoryApi.addBaseAccessories(selectedMaterial.id, addList);
        if (removeList.length) await materialAccessoryApi.removeBaseAccessories(selectedMaterial.id, removeList);
        const res  = await materialAccessoryApi.getBaseAccessories(selectedMaterial.id);
        const list = (res.data || res || []).map((row) => row.accessory ?? row);
        onBaseAdded(list);
      } else {
        if (addList.length)    await materialAccessoryApi.addToMaterial(selectedMaterial.id, selectedType.id, addList);
        if (removeList.length) await materialAccessoryApi.removeFromMaterial(selectedMaterial.id, selectedType.id, removeList);
        const res = await materialAccessoryApi.getForMaterialAndType(selectedMaterial.id, selectedType.id);
        onTypeAdded(res.data || res || []);
      }

      const addedMsg   = addList.length    ? `${addList.length} added`   : '';
      const removedMsg = removeList.length ? `${removeList.length} removed` : '';
      showSnackbar([addedMsg, removedMsg].filter(Boolean).join(', '), 'success');
      onClose();
    } catch (e) {
      showSnackbar(e.response?.data?.message || e.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const titleBg = mode === 'base' ? '#1565C0' : '#2E7D32';

  return (
    <>
      <style>{LIST_STYLE}</style>

      <Dialog
        open={open} onClose={onClose} maxWidth="lg" fullWidth
        PaperProps={{ sx: { height: '88vh', maxHeight: '88vh', display: 'flex', flexDirection: 'column' } }}
      >
        {/* ── Compact title bar ── */}
        <DialogTitle sx={{
          bgcolor: titleBg, color: 'white', fontWeight: 600,
          fontSize: '0.95rem', py: 1.25, px: 2.5, flexShrink: 0,
        }}>
          {mode === 'base' ? 'Add Base Accessories' : 'Add Type-Specific Accessories'}
        </DialogTitle>

        <DialogContent sx={{
          display: 'flex', flexDirection: 'column', gap: 1,
          pt: '10px !important', pb: 1, px: 2, flex: 1, overflow: 'hidden',
        }}>
          {/* ── Compact context info ── */}
          <Box sx={{ px: 1.5, py: 0.75, bgcolor: '#F5F5F5', borderRadius: 1, flexShrink: 0 }}>
            <Typography sx={{ fontSize: '0.82rem' }}>
              Material: <strong>{selectedMaterial?.name}</strong>
            </Typography>
            {mode === 'type' && (
              <Typography sx={{ fontSize: '0.82rem' }}>
                Type: <strong>{selectedType?.name}</strong>
              </Typography>
            )}
            {mode === 'base' && (
              <Typography sx={{ fontSize: '0.75rem', color: '#666', display: 'block' }}>
                Base accessories apply to this material regardless of installation type.
              </Typography>
            )}
          </Box>

          {/* ── Compact search + filter row ── */}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexShrink: 0 }}>
            {/* Search */}
            <TextField
              sx={{
                flex: 2,
                '& .MuiInputBase-input': { py: '5px', fontSize: '0.82rem' },
                '& .MuiInputBase-root': { height: 32 },
              }}
              size="small"
              placeholder="Search accessories..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#bbb', fontSize: 16 }} />
                  </InputAdornment>
                ),
              }}
              inputProps={{ autoComplete: 'off' }}
            />
            {/* Department */}
            <FormControl size="small" sx={{
              flex: 1,
              '& .MuiInputBase-root': { height: 32, fontSize: '0.82rem' },
              '& .MuiInputLabel-root': { fontSize: '0.82rem', top: '-4px' },
              '& .MuiInputLabel-shrink': { top: 0 },
            }}>
              <InputLabel>Department</InputLabel>
              <Select value={deptFilter} onChange={handleDeptChange} label="Department">
                <MenuItem value="" sx={{ fontSize: '0.82rem' }}>All Departments</MenuItem>
                {departments.map((d) => (
                  <MenuItem key={d.id} value={String(d.id)} sx={{ fontSize: '0.82rem' }}>{d.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {/* Group Code */}
            <FormControl size="small" sx={{
              flex: 1,
              '& .MuiInputBase-root': { height: 32, fontSize: '0.82rem' },
              '& .MuiInputLabel-root': { fontSize: '0.82rem', top: '-4px' },
              '& .MuiInputLabel-shrink': { top: 0 },
            }}>
              <InputLabel>Group Code</InputLabel>
              <Select value={groupFilter} onChange={handleGroupChange} label="Group Code">
                <MenuItem value="" sx={{ fontSize: '0.82rem' }}>All Group Codes</MenuItem>
                {groupCodes.map((code) => (
                  <MenuItem key={code} value={code} sx={{ fontSize: '0.82rem' }}>{code}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Progress bar */}
          {fetching && <LinearProgress sx={{ borderRadius: 1, height: 2, flexShrink: 0 }} />}

          {/* Accessories list — fills all remaining vertical space */}
          <div style={{
            border: '1px solid #E0E0E0', borderRadius: 4, overflow: 'hidden',
            flex: 1, display: 'flex', flexDirection: 'column',
          }}>

            {/* ── Pagination bar: "1 – 50 of 535"  <  1  2  3 … 11  > ── */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '4px 12px', background: '#F5F5F5',
              borderBottom: '1px solid #E0E0E0', minHeight: 36,
            }}>
              {/* Count label */}
              <span style={{ fontSize: '0.8rem', color: '#555' }}>
                {displayList.length === 0
                  ? (fetching ? 'Loading…' : 'No accessories available to add')
                  : `${safePage * PAGE_SIZE + 1} – ${Math.min((safePage + 1) * PAGE_SIZE, displayList.length)} of ${displayList.length}`}
                {(checkedIds.size > 0 || removeIds.size > 0) && (
                  <span style={{ marginLeft: 12, fontWeight: 600 }}>
                    {checkedIds.size > 0 && <span style={{ color: titleBg }}>+{checkedIds.size} </span>}
                    {removeIds.size  > 0 && <span style={{ color: '#C62828' }}>−{removeIds.size}</span>}
                  </span>
                )}
              </span>

              {/* Numbered page buttons */}
              {totalPages > 1 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <IconButton
                    size="small" sx={{ p: '3px' }}
                    disabled={safePage === 0}
                    onClick={() => setListPage((p) => p - 1)}
                  >
                    <NavigateBeforeIcon fontSize="small" />
                  </IconButton>

                  {getPageNumbers(safePage, totalPages).map((pg, i) =>
                    pg === '…'
                      ? <span key={`ellipsis-${i}`} style={{ fontSize: '0.75rem', color: '#999', padding: '0 2px' }}>…</span>
                      : (
                        <button
                          key={pg}
                          onClick={() => setListPage(pg)}
                          style={{
                            minWidth: 28, height: 28, border: 'none', borderRadius: 4,
                            fontSize: '0.78rem', cursor: 'pointer', fontFamily: 'inherit',
                            background: pg === safePage ? titleBg : 'transparent',
                            color: pg === safePage ? '#fff' : '#333',
                            fontWeight: pg === safePage ? 700 : 400,
                          }}
                        >
                          {pg + 1}
                        </button>
                      )
                  )}

                  <IconButton
                    size="small" sx={{ p: '3px' }}
                    disabled={safePage >= totalPages - 1}
                    onClick={() => setListPage((p) => p + 1)}
                  >
                    <NavigateNextIcon fontSize="small" />
                  </IconButton>
                </div>
              )}
            </div>

            {/* ── Column header row: [select-all checkbox]  Accessory ── */}
            <div style={{
              display: 'flex', alignItems: 'center',
              padding: '4px 12px 4px 4px',
              background: '#FAFAFA', borderBottom: '1px solid #E0E0E0',
              position: 'sticky', top: 0, zIndex: 1,
            }}>
              <Checkbox
                size="small"
                checked={allVisibleChecked}
                indeterminate={someVisibleChecked}
                onChange={toggleSelectAll}
                tabIndex={-1}
                disableRipple
                sx={{ p: '4px', mr: 1, flexShrink: 0 }}
              />
              <span style={{ fontSize: '0.82rem', fontWeight: 700, color: '#424242' }}>
                Accessory
              </span>
            </div>

            {/* ── Scrollable items — grows to fill all remaining list container height ── */}
            <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
              {displayList.length === 0 ? (
                <Typography sx={{ p: 3, textAlign: 'center', color: '#999' }}>
                  {fetching ? 'Loading…' : 'No accessories available to add'}
                </Typography>
              ) : (
                visibleList.map((acc) => {
                  const isLinked   = linkedIds.has(acc.id);
                  const isToRemove = isLinked && removeIds.has(acc.id);
                  const isChecked  = !isLinked && checkedIds.has(acc.id);

                  let itemClass = 'adlg-item';
                  if (isToRemove)     itemClass += ' adlg-item-toremove';
                  else if (isLinked)  itemClass += ' adlg-item-linked';
                  else if (isChecked) itemClass += ' adlg-item-checked';

                  return (
                    <div
                      key={acc.id}
                      role="button"
                      tabIndex={0}
                      className={itemClass}
                      onClick={() => isLinked ? toggleRemove(acc.id) : toggle(acc.id)}
                      onKeyDown={(e) => (e.key === ' ' || e.key === 'Enter') && (isLinked ? toggleRemove(acc.id) : toggle(acc.id))}
                    >
                      {isToRemove ? (
                        <RemoveCircleIcon sx={{ color: '#C62828', fontSize: 18, mr: 1, flexShrink: 0 }} />
                      ) : isLinked ? (
                        <CheckCircleIcon sx={{ color: '#2E7D32', fontSize: 18, mr: 1, flexShrink: 0 }} />
                      ) : (
                        <Checkbox
                          checked={isChecked}
                          tabIndex={-1}
                          disableRipple
                          size="small"
                          sx={{ p: '4px', mr: 1, flexShrink: 0 }}
                        />
                      )}
                      <div>
                        <div className="adlg-primary" style={
                          isToRemove ? { color: '#C62828', textDecoration: 'line-through' }
                          : isLinked ? { color: '#2E7D32' }
                          : undefined
                        }>
                          {acc.name}
                        </div>
                        <div className="adlg-secondary" style={{
                          color: isToRemove ? '#E53935' : isLinked ? '#4CAF50' : undefined
                        }}>
                          {isToRemove ? 'Click again to keep' : isLinked ? 'Already added · click to remove' : ''}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </DialogContent>

        <DialogActions sx={{ px: 2, py: 1.25, flexShrink: 0, borderTop: '1px solid #E0E0E0' }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAdd}
            disabled={(checkedIds.size === 0 && removeIds.size === 0) || saving}
            sx={{ backgroundColor: removeIds.size > 0 && checkedIds.size === 0 ? '#C62828' : titleBg }}
          >
            {saving ? 'Saving…' : (() => {
              const a = checkedIds.size > 0 ? `Add ${checkedIds.size}` : '';
              const r = removeIds.size  > 0 ? `Remove ${removeIds.size}` : '';
              return [a, r].filter(Boolean).join(' · ') || 'Apply Changes';
            })()}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
});

export default AddAccessoriesDialog;
